/**
 * Bulk action modals for schedule collaboration.
 * Split from ScheduleModals.tsx to keep each file under 500 lines.
 */
import { useState } from 'react';
import { Button } from '../../../ui/button';
import { Textarea } from '../../../ui/textarea';
import { Label } from '../../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../ui/dialog';
import { Loader2 } from 'lucide-react';
import { ReasonCodeSelect, getEffectiveReason } from '../../AcknowledgmentSharedUtils';

interface BulkAcceptModalProps {
  open: boolean;
  count: number;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function BulkAcceptModal({ open, count, loading, onClose, onConfirm }: BulkAcceptModalProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Accept Schedules</DialogTitle>
          <DialogDescription>
            Are you sure you want to accept {count} schedule{count > 1 ? 's' : ''}?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={onConfirm} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Accept {count} Schedule{count > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BulkRejectModalProps {
  open: boolean;
  count: number;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export function BulkRejectModal({ open, count, loading, onClose, onConfirm }: BulkRejectModalProps) {
  const [reason, setReason] = useState('');
  const handleClose = () => { setReason(''); onClose(); };
  const handleSubmit = () => { if (reason.trim()) onConfirm(reason.trim()); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Schedules</DialogTitle>
          <DialogDescription>
            Provide a reason for rejecting {count} schedule{count > 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="bulk-reject-reason">Reason *</Label>
            <Textarea
              id="bulk-reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for rejecting..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={loading || !reason.trim()}>
            {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Reject {count} Schedule{count > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BulkHoldModalProps {
  open: boolean;
  count: number;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export function BulkHoldModal({ open, count, loading, onClose, onConfirm }: BulkHoldModalProps) {
  // Customer review 2026-07-08 (Vaida): admin-configurable hold reasons
  // (reason-code category 'hold') + Other free text — same as the per-row
  // Put On Hold modal.
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const handleClose = () => { setReasonSelect(''); setCustomReason(''); onClose(); };
  const effectiveReason = getEffectiveReason(reasonSelect, customReason);
  const handleSubmit = () => { if (effectiveReason) onConfirm(effectiveReason); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put Schedules On Hold</DialogTitle>
          <DialogDescription>
            Provide a reason for putting {count} schedule{count > 1 ? 's' : ''} on hold.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <ReasonCodeSelect
            value={reasonSelect}
            onChange={setReasonSelect}
            customValue={customReason}
            onCustomChange={setCustomReason}
            required
            label="Reason"
            categories={['hold']}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !effectiveReason} className="bg-orange-600 hover:bg-orange-700">
            {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Put {count} Schedule{count > 1 ? 's' : ''} On Hold
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
